import { useEffect, useMemo, useState } from "react";
import { Calculator as CalcIcon, Info } from "lucide-react";
import { track } from "@/lib/tracker";
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";

export const CALC_NOTE =
  "Ceci est une illustration simplifiée, pas une promesse de rendement / un avis fiscal. Seule une rencontre avec un conseiller permet d'établir vos chiffres réels.";

const fmt = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(Math.round(n || 0));
const pct = (n) => `${(n * 100).toFixed(1).replace(".", ",")} %`;
const grow = (initial, annual, years, g) => { let v = initial; for (let y = 0; y < years; y++) v = (v + annual) * (1 + g); return v; };

// Déviations annuelles déterministes (cycles de marché) pour une allure réaliste en ZIG-ZAG.
// Elles sont recentrées à moyenne ~0 sur la durée : le rendement MOYEN reste l'hypothèse choisie,
// mais le chemin année par année monte et descend comme un vrai marché.
const MARKET_DEV = [9, -7, 15, 3, -12, 20, 6, -5, 13, 23, -10, 8, 17, -14, 11, 5, 25, -8, 12, -4, 16, 9, -11, 19, 4, -6, 14, 8, -9, 21];
const fmtK = (n) => { const a = Math.abs(n); if (a >= 1e6) return (n / 1e6).toFixed(1).replace(".", ",") + " M"; if (a >= 1000) return Math.round(n / 1000) + " k"; return String(Math.round(n)); };
const growthSeries = (initial, annual, years, growthPct, feePct, extraPerYear = 0) => {
  const win = Math.min(years, MARKET_DEV.length) || 1;
  const mean = MARKET_DEV.slice(0, win).reduce((a, b) => a + b, 0) / win;
  let bal = initial, contrib = initial;
  const pts = [{ year: 0, cotise: Math.round(contrib), interets: 0, valeur: Math.round(bal) }];
  for (let y = 0; y < years; y++) {
    contrib += annual;
    bal += annual + extraPerYear;
    const r = (growthPct - feePct + (MARKET_DEV[y % MARKET_DEV.length] - mean)) / 100;
    bal = Math.max(0, bal * (1 + r));
    pts.push({ year: y + 1, cotise: Math.round(contrib), interets: Math.round(bal - contrib), valeur: Math.round(bal) });
  }
  return pts;
};

const FEDERAL = [[0, 58523, 0.14], [58523, 117045, 0.205], [117045, 181440, 0.26], [181440, 258482, 0.29], [258482, Infinity, 0.33]];
const QC = [[0, 54345, 0.14], [54345, 108680, 0.19], [108680, 132245, 0.24], [132245, Infinity, 0.2575]];
const ABATEMENT = 0.165;
const FEE = { key: "fee", label: "Frais de gestion annuels", min: 0, max: 2.5, step: 0.1, def: 1.5, suffix: " %" };
const FEE_FD = { key: "fee", label: "Frais de gestion annuels", min: 0, max: 4, step: 0.1, def: 2.5, suffix: " %" };

function taxFrom(income, brackets) {
  let tax = 0;
  for (const [lo, hi, rate] of brackets) if (income > lo) tax += (Math.min(income, hi) - lo) * rate;
  return tax;
}
function marginal(income, brackets) {
  for (const [lo, hi, rate] of brackets) if (income > lo && income <= hi) return rate;
  return brackets[brackets.length - 1][2];
}

// Chaque calculateur expose ses hypothèses (hyp) pour la transparence conseiller/AMF.
const CONFIGS = {
  reee: {
    hyp: "Cotisation en début d'année. SCEE 20 % (max 7 200 $/enfant), IQEE 10 % (max 3 600 $/enfant), subventions calculées sur les premiers 2 500 $ cotisés. Croissance composée annuelle sur cotisations + subventions.",
    inputs: [
      { key: "initial", label: "Montant de départ (déjà épargné)", min: 0, max: 50000, step: 500, def: 0, money: true },
      { key: "monthly", label: "Cotisation mensuelle", min: 0, max: 500, step: 10, def: 208, money: true },
      { key: "years", label: "Nombre d'années", min: 1, max: 18, step: 1, def: 17 },
      { key: "growth", label: "Rendement annuel", min: 0, max: 8, step: 0.5, def: 5, suffix: " %" },
      { ...FEE },
    ],
    series: ({ initial, monthly, years, growth, fee }) => growthSeries(initial, monthly * 12, years, growth, fee, Math.min(monthly * 12, 2500) * 0.3),
    compute: ({ initial, monthly, years, growth, fee }) => {
      const gNet = Math.max(0, growth - fee) / 100, gGross = growth / 100, annual = monthly * 12;
      let scee = 0, iqee = 0, contributed = initial, grants = 0, value = initial, valueGross = initial;
      for (let y = 1; y <= years; y++) {
        contributed += annual;
        const base = Math.min(annual, 2500);
        const sc = Math.min(base * 0.2, 7200 - scee); scee += sc;
        const iq = Math.min(base * 0.1, 3600 - iqee); iqee += iq;
        grants += sc + iq;
        value = (value + annual + sc + iq) * (1 + gNet);
        valueGross = (valueGross + annual + sc + iq) * (1 + gGross);
      }
      return [
        { label: "Total cotisé (départ + mensuel)", value: fmt(contributed) },
        { label: "Subventions reçues", value: fmt(grants), accent: true },
        { label: "Intérêts gagnés (croissance)", value: fmt(Math.max(0, value - contributed - grants)) },
        { label: "Valeur estimée du REEE", value: fmt(value), big: true },
        { label: "Coût des frais sur la période", value: fmt(valueGross - value), danger: true },
      ];
    },
  },
  fiscalite: {
    hyp: "Paliers 2026 fédéraux (avec abattement québécois de 16,5 %) et provinciaux du Québec. Ne tient pas compte des crédits personnels ni des déductions (REER, etc.), qui réduisent l'impôt réel.",
    inputs: [{ key: "revenu", label: "Revenu annuel imposable", min: 0, max: 300000, step: 1000, def: 75000, money: true }],
    compute: ({ revenu }) => {
      const fed = taxFrom(revenu, FEDERAL) * (1 - ABATEMENT);
      const prov = taxFrom(revenu, QC);
      const total = fed + prov;
      const marg = marginal(revenu, FEDERAL) * (1 - ABATEMENT) + marginal(revenu, QC);
      return [
        { label: "Impôt fédéral (après abattement)", value: fmt(fed) },
        { label: "Impôt provincial (Québec)", value: fmt(prov) },
        { label: "Impôt total estimé", value: fmt(total), accent: true },
        { label: "Revenu net", value: fmt(revenu - total), big: true },
        { label: "Taux marginal", value: pct(marg) },
        { label: "Taux moyen", value: revenu ? pct(total / revenu) : "0 %" },
      ];
    },
  },
  celi: {
    hyp: "Dépôts en début d'année, croissance composée. Le compte imposable comparatif suppose que la croissance est imposée chaque année à votre taux marginal (aucun gain reporté).",
    inputs: [
      { key: "initial", label: "Montant de départ", min: 0, max: 100000, step: 1000, def: 0, money: true },
      { key: "monthly", label: "Dépôt mensuel", min: 0, max: 583, step: 10, def: 300, money: true },
      { key: "years", label: "Durée (années)", min: 1, max: 40, step: 1, def: 20 },
      { key: "growth", label: "Rendement annuel", min: 0, max: 8, step: 0.5, def: 5, suffix: " %" },
      { key: "rate", label: "Taux marginal", min: 15, max: 53, step: 1, def: 37, suffix: " %" },
      { ...FEE },
    ],
    series: ({ initial, monthly, years, growth, fee }) => growthSeries(initial, monthly * 12, years, growth, fee),
    compute: ({ initial, monthly, years, growth, rate, fee }) => {
      const gNet = Math.max(0, growth - fee) / 100, gGross = growth / 100, annual = monthly * 12, gt = gNet * (1 - rate / 100);
      const celi = grow(initial, annual, years, gNet), celiGross = grow(initial, annual, years, gGross), taxable = grow(initial, annual, years, gt);
      const contributed = initial + annual * years;
      return [
        { label: "Total cotisé (départ + mensuel)", value: fmt(contributed) },
        { label: "Intérêts gagnés (libres d'impôt)", value: fmt(celi - contributed), accent: true },
        { label: "Valeur en CELI", value: fmt(celi), big: true },
        { label: "Compte imposable équivalent", value: fmt(taxable) },
        { label: "Écart en votre faveur", value: fmt(celi - taxable), accent: true },
        { label: "Coût des frais sur la période", value: fmt(celiGross - celi), danger: true },
      ];
    },
  },
  reer: {
    hyp: "Cotisation en début d'année, croissance composée. Le remboursement d'impôt correspond à la cotisation × votre taux marginal. Le coût net suppose ce remboursement réinvesti hors calcul.",
    inputs: [
      { key: "initial", label: "Montant de départ (REER actuel)", min: 0, max: 200000, step: 1000, def: 0, money: true },
      { key: "monthly", label: "Cotisation mensuelle", min: 0, max: 2800, step: 50, def: 500, money: true },
      { key: "rate", label: "Taux marginal", min: 15, max: 53, step: 1, def: 37, suffix: " %" },
      { key: "years", label: "Durée (années)", min: 1, max: 40, step: 1, def: 25 },
      { key: "growth", label: "Rendement annuel", min: 0, max: 8, step: 0.5, def: 5, suffix: " %" },
      { ...FEE },
    ],
    series: ({ initial, monthly, years, growth, fee }) => growthSeries(initial, monthly * 12, years, growth, fee),
    compute: ({ initial, monthly, rate, years, growth, fee }) => {
      const gNet = Math.max(0, growth - fee) / 100, gGross = growth / 100, annual = monthly * 12;
      const value = grow(initial, annual, years, gNet), valueGross = grow(initial, annual, years, gGross);
      const contributed = initial + annual * years;
      return [
        { label: "Total cotisé (départ + mensuel)", value: fmt(contributed) },
        { label: "Intérêts gagnés (croissance)", value: fmt(value - contributed), accent: true },
        { label: "Remboursement d'impôt (par année)", value: fmt(annual * (rate / 100)) },
        { label: "Valeur estimée à la retraite", value: fmt(value), big: true },
        { label: "Coût des frais sur la période", value: fmt(valueGross - value), danger: true },
      ];
    },
  },
  celiapp: {
    hyp: "Cotisation en début d'année (max utile 8 000 $/an, 40 000 $ à vie). Remboursement d'impôt = cotisations × taux marginal. Croissance composée jusqu'à l'achat.",
    inputs: [
      { key: "initial", label: "Montant de départ (CELIAPP actuel)", min: 0, max: 40000, step: 500, def: 0, money: true },
      { key: "monthly", label: "Cotisation mensuelle", min: 0, max: 666, step: 10, def: 666, money: true },
      { key: "years", label: "Années (max 5 utiles)", min: 1, max: 5, step: 1, def: 5 },
      { key: "rate", label: "Taux marginal", min: 15, max: 53, step: 1, def: 37, suffix: " %" },
      { key: "growth", label: "Rendement annuel", min: 0, max: 8, step: 0.5, def: 4, suffix: " %" },
      { ...FEE },
    ],
    series: ({ initial, monthly, years, growth, fee }) => growthSeries(initial, monthly * 12, years, growth, fee),
    compute: ({ initial, monthly, years, rate, growth, fee }) => {
      const gNet = Math.max(0, growth - fee) / 100, gGross = growth / 100, annual = monthly * 12;
      const value = grow(initial, annual, years, gNet), valueGross = grow(initial, annual, years, gGross);
      const contributed = initial + annual * years;
      return [
        { label: "Total cotisé (départ + mensuel)", value: fmt(contributed) },
        { label: "Intérêts gagnés (croissance)", value: fmt(value - contributed), accent: true },
        { label: "Remboursement d'impôt total", value: fmt(contributed * (rate / 100)), accent: true },
        { label: "Mise de fonds estimée (avec croissance)", value: fmt(value), big: true },
        { label: "Coût des frais sur la période", value: fmt(valueGross - value), danger: true },
      ];
    },
  },
  vie: {
    hyp: "Méthode des besoins : remplacement du revenu sur N années + dettes et frais finaux − épargne et assurances existantes. Repère courant de 8 à 10× le revenu en l'absence de détails.",
    inputs: [
      { key: "revenu", label: "Revenu annuel", min: 20000, max: 250000, step: 5000, def: 60000, money: true },
      { key: "annees", label: "Années à protéger", min: 1, max: 25, step: 1, def: 10 },
      { key: "dettes", label: "Dettes + hypothèque + frais finaux", min: 0, max: 800000, step: 10000, def: 250000, money: true },
      { key: "epargne", label: "Épargne / assurances existantes", min: 0, max: 800000, step: 10000, def: 50000, money: true },
    ],
    compute: ({ revenu, annees, dettes, epargne }) => {
      const besoin = Math.max(0, revenu * annees + dettes - epargne);
      return [
        { label: "Remplacement de revenu", value: fmt(revenu * annees) },
        { label: "Dettes et frais à couvrir", value: fmt(dettes) },
        { label: "Moins l'épargne existante", value: `− ${fmt(epargne)}` },
        { label: "Capital à considérer", value: fmt(besoin), big: true, accent: true },
      ];
    },
  },
  invalidite: {
    hyp: "Le régime public / collectif remplace un pourcentage ajustable de votre revenu net (souvent 30–67 %, imposable). L'écart illustré est le manque à combler chaque mois.",
    inputs: [
      { key: "income", label: "Revenu mensuel net", min: 1500, max: 15000, step: 100, def: 4000, money: true },
      { key: "couvert", label: "Part remplacée (public/collectif)", min: 20, max: 67, step: 1, def: 40, suffix: " %" },
    ],
    compute: ({ income, couvert }) => {
      const publicPart = income * (couvert / 100);
      return [
        { label: "Estimation remplacée", value: fmt(publicPart) },
        { label: "Écart à combler chaque mois", value: fmt(income - publicPart), big: true, accent: true },
        { label: "Écart sur 1 an", value: fmt((income - publicPart) * 12) },
      ];
    },
  },
  maladiesgraves: {
    hyp: "Montant forfaitaire couvrant vos dépenses courantes pendant la convalescence, plus un coussin pour soins et pertes de revenu non couverts.",
    inputs: [
      { key: "monthly", label: "Dépenses mensuelles", min: 1000, max: 12000, step: 250, def: 4000, money: true },
      { key: "months", label: "Mois de tranquillité", min: 6, max: 36, step: 1, def: 12 },
      { key: "extra", label: "Coussin soins / imprévus", min: 0, max: 100000, step: 5000, def: 20000, money: true },
    ],
    compute: ({ monthly, months, extra }) => [
      { label: `${months} mois × ${fmt(monthly)}`, value: fmt(monthly * months) },
      { label: "Coussin additionnel", value: fmt(extra) },
      { label: "Montant forfaitaire à considérer", value: fmt(monthly * months + extra), big: true, accent: true },
    ],
  },
  sld: {
    hyp: "Coût mensuel net à votre charge (après contribution publique éventuelle) multiplié par la durée estimée des soins.",
    inputs: [
      { key: "monthly", label: "Coût mensuel à votre charge", min: 1000, max: 12000, step: 250, def: 5000, money: true },
      { key: "years", label: "Durée estimée (années)", min: 1, max: 15, step: 1, def: 4 },
    ],
    compute: ({ monthly, years }) => [
      { label: "Coût annuel", value: fmt(monthly * 12) },
      { label: "Exposition totale estimée", value: fmt(monthly * 12 * years), big: true, accent: true },
    ],
  },
  fondsdistincts: {
    hyp: "Croissance composée sur le dépôt initial et les cotisations mensuelles, nette des frais de gestion. La garantie (75 % ou 100 %) s'applique aux dépôts à l'échéance du contrat ou au décès, jamais à un retrait anticipé.",
    inputs: [
      { key: "deposit", label: "Dépôt initial", min: 0, max: 500000, step: 5000, def: 50000, money: true },
      { key: "monthly", label: "Cotisation mensuelle", min: 0, max: 5000, step: 50, def: 0, money: true },
      { key: "years", label: "Durée (années)", min: 1, max: 30, step: 1, def: 10 },
      { key: "growth", label: "Rendement annuel", min: 0, max: 8, step: 0.5, def: 5, suffix: " %" },
      { key: "garantie", label: "Niveau de garantie", min: 75, max: 100, step: 25, def: 75, suffix: " %" },
      { ...FEE_FD },
    ],
    series: ({ deposit, monthly, years, growth, fee }) => growthSeries(deposit, monthly * 12, years, growth, fee),
    compute: ({ deposit, monthly, years, growth, garantie, fee }) => {
      const gNet = Math.max(0, growth - fee) / 100, gGross = growth / 100, annual = monthly * 12;
      const value = grow(deposit, annual, years, gNet), valueGross = grow(deposit, annual, years, gGross);
      const contributed = deposit + annual * years;
      return [
        { label: "Total cotisé (dépôt + mensuel)", value: fmt(contributed) },
        { label: "Intérêts gagnés (croissance)", value: fmt(value - contributed), accent: true },
        { label: "Valeur estimée à l'échéance", value: fmt(value), big: true },
        { label: `Plancher garanti (${garantie} %)`, value: fmt(contributed * (garantie / 100)) },
        { label: "Coût des frais sur la période", value: fmt(valueGross - value), danger: true },
      ];
    },
  },
  rentes: {
    hyp: "Taux de rente illustratif variant avec l'âge d'achat (plus l'âge est élevé, plus le revenu est élevé). Rente viagère simple, sans indexation. Les taux réels varient chaque jour selon l'assureur.",
    inputs: [
      { key: "capital", label: "Capital à convertir", min: 25000, max: 1000000, step: 25000, def: 200000, money: true },
      { key: "age", label: "Âge à l'achat", min: 60, max: 80, step: 1, def: 65 },
    ],
    compute: ({ capital, age }) => {
      const rate = Math.max(0.04, Math.min(0.09, 0.048 + (age - 65) * 0.003));
      const annual = capital * rate;
      return [
        { label: `Taux illustratif (~${(rate * 100).toFixed(1).replace(".", ",")} %)`, value: fmt(annual), accent: true },
        { label: "Revenu mensuel illustratif", value: fmt(annual / 12), big: true },
      ];
    },
  },
};

export const Calculator = ({ type, accent = "#012141" }) => {
  const cfg = CONFIGS[type];
  const [values, setValues] = useState(() => Object.fromEntries((cfg?.inputs || []).map((i) => [i.key, i.def])));
  useEffect(() => { if (CONFIGS[type]) track("calculator_use", { label: type, meta: { type } }); }, [type]);
  const outputs = useMemo(() => (cfg ? cfg.compute(values) : []), [cfg, values]);
  const chart = useMemo(() => (cfg?.series ? cfg.series(values) : null), [cfg, values]);
  if (!cfg) return null;

  return (
    <div data-testid={`calculator-${type}`} className="rounded-2xl border border-[#012141]/15 bg-white p-5 sm:p-6 shadow-sm">
      <div className="flex items-center gap-2 text-sm font-bold" style={{ color: accent }}><CalcIcon size={18} /> Calculateur interactif</div>
      <div className="mt-4 space-y-4">
        {cfg.inputs.map((inp) => (
          <div key={inp.key}>
            <div className="flex items-center justify-between text-sm">
              <label className="font-semibold text-slate-700">{inp.label}</label>
              <span className="font-bold" style={{ color: accent }}>{inp.money ? fmt(values[inp.key]) : `${values[inp.key]}${inp.suffix || ""}`}</span>
            </div>
            <input data-testid={`calc-input-${inp.key}`} type="range" min={inp.min} max={inp.max} step={inp.step} value={values[inp.key]}
              onChange={(e) => setValues((v) => ({ ...v, [inp.key]: parseFloat(e.target.value) }))} className="w-full mt-2 accent-[#C89A3E]" />
          </div>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {outputs.map((o, i) => (
          <div key={i} data-testid={`calc-output-${i}`} className={`rounded-xl p-4 ${o.big ? "sm:col-span-2" : ""}`} style={{ background: o.danger ? "#B23A4815" : o.accent ? "#C89A3E18" : "#0121410D" }}>
            <div className="text-xs font-semibold text-slate-500">{o.label}</div>
            <div className={`font-heading font-bold ${o.big ? "text-3xl" : "text-xl"}`} style={{ color: o.danger ? "#B23A48" : o.accent ? "#8A6A1E" : accent }}>{o.value}</div>
          </div>
        ))}
      </div>
      {chart && chart.length > 1 && (
        <div className="mt-6" data-testid={`calc-chart-${type}`}>
          <div className="text-sm font-bold mb-1" style={{ color: accent }}>Évolution année par année</div>
          <div className="text-xs text-slate-500 mb-3">Cotisations vs valeur du placement, avec des variations de marché illustrées (le rendement moyen reste votre hypothèse). L'écart entre la ligne et l'aire = intérêts cumulés.</div>
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={chart} margin={{ top: 5, right: 6, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gCot" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={accent} stopOpacity={0.2} /><stop offset="100%" stopColor={accent} stopOpacity={0.02} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#0121410F" vertical={false} />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#01214122" }} label={{ value: "années", position: "insideBottomRight", offset: -2, fontSize: 10, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={52} tickFormatter={fmtK} />
              <Tooltip formatter={(v, n) => [fmt(v), n]} labelFormatter={(l) => `Année ${l}`} contentStyle={{ borderRadius: 12, border: "1px solid #01214122", fontSize: 12 }} />
              <Area type="monotone" dataKey="cotise" name="Cotisations" stroke={accent} strokeWidth={1.5} fill="url(#gCot)" />
              <Line type="monotone" dataKey="valeur" name="Valeur (marché)" stroke="#C89A3E" strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm" style={{ background: accent, opacity: 0.3 }} /> Cotisations</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-4 h-0.5 bg-[#C89A3E]" /> Valeur avec variations de marché</span>
          </div>
        </div>
      )}
      <div className="mt-4 space-y-2 text-xs text-slate-500 leading-relaxed">
        {cfg.hyp && <div className="flex items-start gap-2"><span className="font-semibold text-slate-600 flex-shrink-0">Hypothèses :</span><span>{cfg.hyp}</span></div>}
        <div className="flex items-start gap-2"><Info size={14} className="mt-0.5 flex-shrink-0" /><span>{CALC_NOTE}</span></div>
      </div>
    </div>
  );
};
